import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Loader2, Sparkles, FileDown, RotateCcw, Save, Wand2 } from "lucide-react";
import { API } from "../App";
import { downloadWorksheetPdf } from "./WorksheetEditorDialog";

const exToText = (ex) => (Array.isArray(ex) ? ex.join(" | ") : ex || "");
const textToEx = (sec, v) => (sec.kind === "table" ? v.split("|").map((x) => x.trim()) : v);
const lines = (a) => (a || []).join("\n");
const fromLines = (v) => v.split("\n").map((x) => x.trim()).filter(Boolean);

const SectionEditor = ({ sec, i, full, onChange }) => (
  <div className="rounded-lg border p-3 space-y-2 bg-background" data-testid={`custom-section-${i}`}>
    <p className="text-sm font-medium">{sec.title} <span className="text-[10px] px-1.5 rounded-full bg-muted text-muted-foreground ml-1">{sec.kind}</span></p>
    {full ? (
      <Input value={sec.hint} onChange={(e) => onChange({ ...sec, hint: e.target.value })} placeholder="Consigne de la zone" className="h-8 text-sm" data-testid={`custom-hint-${i}`} />
    ) : sec.hint && <p className="text-xs text-muted-foreground">{sec.hint}</p>}
    {sec.kind !== "scale" && sec.kind !== "checklist" && (
      <label className="block text-xs text-muted-foreground">Exemple grisé{sec.kind === "table" ? " (une valeur par colonne, séparées par « | »)" : ""}
        <Textarea value={exToText(sec.example)} onChange={(e) => onChange({ ...sec, example: textToEx(sec, e.target.value) })} rows={2} className="mt-1 text-sm italic" data-testid={`custom-example-${i}`} />
      </label>
    )}
  </div>
);

export const WorksheetPersonalizeDialog = ({ open, onOpenChange, exerciseType, exerciseTitle, patientId, patientName, onSaved }) => {
  const [data, setData] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [instructions, setInstructions] = useState("");
  const [depth, setDepth] = useState("examples");
  const [busy, setBusy] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!open || !exerciseType) return;
    setData(null); setSheet(null); setDirty(false); setBusy("load");
    axios.get(`${API}/exercise-worksheets/${exerciseType}/custom/${patientId}`, { withCredentials: true })
      .then((r) => { setData(r.data); setSheet(r.data.custom?.sheet || r.data.base); setInstructions(r.data.custom?.instructions || ""); setDepth(r.data.custom?.depth || "examples"); })
      .catch((e) => toast.error(e.response?.data?.detail || "Fiche indisponible"))
      .finally(() => setBusy(""));
  }, [open, exerciseType, patientId]);

  const update = (s) => { setSheet(s); setDirty(true); };
  const personalize = async () => {
    setBusy("ai");
    try {
      const r = await axios.post(`${API}/exercise-worksheets/${exerciseType}/personalize`, { patient_id: patientId, instructions, depth, sheet: data?.base }, { withCredentials: true });
      update(r.data.sheet);
      toast.success(r.data.has_context ? "Fiche adaptée à la situation du patient — relisez avant d'imprimer" : "Fiche adaptée (peu d'éléments dans le dossier : exemples restés généraux)");
    } catch (e) { toast.error(e.response?.data?.detail || "Personnalisation impossible"); } finally { setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try {
      const r = await axios.put(`${API}/exercise-worksheets/${exerciseType}/custom/${patientId}`, { sheet, instructions, depth }, { withCredentials: true });
      setData((d) => ({ ...d, custom: r.data })); setDirty(false); onSaved?.(); toast.success("Fiche personnalisée enregistrée pour ce patient");
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible"); return false; } finally { setBusy(""); }
  };
  const pdf = async () => {
    if (dirty || !data?.custom) { if (!(await save())) return; }
    setBusy("pdf");
    try { await downloadWorksheetPdf(exerciseType, { patient_id: patientId }, `fiche-${exerciseType}-${patientName || "patient"}.pdf`); }
    catch { toast.error("PDF indisponible"); } finally { setBusy(""); }
  };
  const reset = async () => {
    setBusy("reset");
    try { await axios.delete(`${API}/exercise-worksheets/${exerciseType}/custom/${patientId}`, { withCredentials: true }); setSheet(data.base); setData((d) => ({ ...d, custom: null })); setDirty(false); onSaved?.(); toast.success("Fiche standard rétablie"); }
    catch { toast.error("Erreur"); } finally { setBusy(""); }
  };
  const full = depth === "full";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="worksheet-personalize-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wand2 className="w-5 h-5 text-primary" /> Fiche sur mesure — {exerciseTitle}</DialogTitle>
          <DialogDescription>Pour {patientName} · {data ? `${data.lang.toUpperCase()} · ${data.audience === "ado" ? "version ados (tutoiement)" : "version adulte"}` : "…"}{data?.custom ? " · fiche personnalisée enregistrée" : ""}</DialogDescription>
        </DialogHeader>
        {busy === "load" || !sheet ? <div className="py-10 text-center text-sm text-muted-foreground">Chargement de la fiche…</div> : (
          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
              <label className="block text-sm font-medium">Consignes pour l'IA (facultatif)
                <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={2} className="mt-1 text-sm" placeholder="Ex. : centrer les exemples sur les disputes du soir avec sa fille ; éviter le thème du travail" data-testid="custom-instructions" />
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex rounded-lg border overflow-hidden text-xs" data-testid="custom-depth">
                  <button onClick={() => setDepth("examples")} className={`px-3 py-1.5 ${!full ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="custom-depth-examples">Exemples grisés seulement</button>
                  <button onClick={() => setDepth("full")} className={`px-3 py-1.5 border-l ${full ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="custom-depth-full">+ consignes et conseils</button>
                </div>
                <Button size="sm" onClick={personalize} disabled={busy !== ""} className="gap-1.5" data-testid="custom-ai-btn">{busy === "ai" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Adapter à la situation du patient (IA)</Button>
              </div>
              <p className="text-xs text-muted-foreground">Sources : dossier validé (mots-clés, difficultés, attentes), 3 dernières séances (résumé, travail personnel, analyse ABC). La structure de la fiche n'est jamais modifiée. Relisez toujours avant d'imprimer.</p>
            </div>
            {full && (
              <label className="block text-xs text-muted-foreground">À quoi sert l'exercice
                <Textarea value={sheet.purpose} onChange={(e) => update({ ...sheet, purpose: e.target.value })} rows={2} className="mt-1 text-sm" data-testid="custom-purpose" />
              </label>
            )}
            <div className="space-y-2">
              {sheet.sections.map((sec, i) => <SectionEditor key={i} sec={sec} i={i} full={full} onChange={(ns) => update({ ...sheet, sections: sheet.sections.map((x, j) => (j === i ? ns : x)) })} />)}
            </div>
            {full && (
              <label className="block text-xs text-muted-foreground">Conseils (un par ligne)
                <Textarea value={lines(sheet.tips)} onChange={(e) => update({ ...sheet, tips: fromLines(e.target.value) })} rows={3} className="mt-1 text-sm" data-testid="custom-tips" />
              </label>
            )}
            <div className="flex flex-wrap justify-between gap-2 pt-2 border-t">
              <Button variant="ghost" size="sm" onClick={reset} disabled={busy !== "" || !data?.custom} className="gap-1.5 text-muted-foreground" data-testid="custom-reset-btn"><RotateCcw className="w-3.5 h-3.5" /> Rétablir la fiche standard</Button>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={save} disabled={busy !== "" || !dirty} className="gap-1.5" data-testid="custom-save-btn">{busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer</Button>
                <Button size="sm" onClick={pdf} disabled={busy !== ""} className="gap-1.5" data-testid="custom-pdf-btn">{busy === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} Télécharger le PDF</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
